import type { PlaybookParams, PlaybookRef, PlaybookPatch, PlaybookRule } from '@reelmachine/core';

/** The objective is a log ratio against the channel's typical reel: exp(x) - 1 is the relative difference. */
export function formatObjective(x: number | undefined): string {
  if (x === undefined || !Number.isFinite(x)) return 'n/a';
  const pct = Math.round((Math.exp(x) - 1) * 100);
  return `${pct > 0 ? '+' : pct < 0 ? '−' : '±'}${Math.abs(pct)} % vs. typical`;
}

export function formatPercent(x: number | undefined): string {
  return x === undefined || !Number.isFinite(x) ? 'n/a' : `${Math.round(x * 100)} %`;
}

export function formatUsd(x: number): string {
  return `$${x.toFixed(2)}`;
}

export function daysSince(iso: string, now: number = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000));
}

export const FIELD_LABEL: Record<keyof PlaybookParams, string> = {
  addenda: 'Step guidance',
  rules: 'Rules',
  narrativeBias: 'Narrative bias',
  profileShift: 'Video profile shift',
  hookTypeWeights: 'Hook type weights',
  effects: 'Effects',
  lengthS: 'Target length',
  panel: 'Panel',
  examples: 'Examples',
  models: 'Models per step',
  pipelineShape: 'Pipeline shape',
};

export interface FieldChange {
  field: keyof PlaybookParams;
  label: string;
  lines: Array<{ kind: 'add' | 'remove' | 'change'; text: string }>;
}

const show = (v: unknown): string => {
  if (v === undefined) return 'not set';
  if (Array.isArray(v)) return v.length ? v.map((x) => show(x)).join(', ') : 'none';
  if (v && typeof v === 'object') {
    const e = Object.entries(v);
    return e.length ? e.map(([k, x]) => `${k}: ${show(x)}`).join(', ') : 'none';
  }
  return String(v);
};

function diffRecord(before: Record<string, unknown>, after: Record<string, unknown>): FieldChange['lines'] {
  const lines: FieldChange['lines'] = [];
  for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const a = before[k];
    const b = after[k];
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    if (a === undefined) lines.push({ kind: 'add', text: `${k}: ${show(b)}` });
    else if (b === undefined) lines.push({ kind: 'remove', text: `${k}: ${show(a)}` });
    else lines.push({ kind: 'change', text: `${k}: ${show(a)} → ${show(b)}` });
  }
  return lines;
}

function diffRules(before: PlaybookRule[], after: PlaybookRule[]): FieldChange['lines'] {
  const lines: FieldChange['lines'] = [];
  const old = new Map(before.map((r) => [r.id, r]));
  const now = new Map(after.map((r) => [r.id, r]));
  for (const r of after) {
    const o = old.get(r.id);
    if (!o) lines.push({ kind: 'add', text: `${r.id} (${r.status}): ${r.text}` });
    else if (JSON.stringify(o) !== JSON.stringify(r)) {
      lines.push({ kind: 'change', text: `${r.id}: ${o.status === r.status ? r.text : `${o.status} → ${r.status}`}` });
    }
  }
  for (const r of before) if (!now.has(r.id)) lines.push({ kind: 'remove', text: `${r.id}: ${r.text}` });
  return lines;
}

/** Readable field-by-field diff of a patch against the current params. Unchanged fields are left out. */
export function describePatch(current: PlaybookParams | undefined, patch: PlaybookPatch): FieldChange[] {
  const out: FieldChange[] = [];
  for (const field of Object.keys(patch) as Array<keyof PlaybookParams>) {
    const before = current?.[field];
    const after = patch[field];
    if (JSON.stringify(before) === JSON.stringify(after)) continue;
    let lines: FieldChange['lines'];
    if (field === 'rules') lines = diffRules((before as PlaybookRule[]) ?? [], (after as PlaybookRule[]) ?? []);
    else if (field === 'lengthS' || field === 'profileShift' || typeof after !== 'object' || after === null) {
      lines = [{ kind: before === undefined ? 'add' : 'change', text: `${show(before)} → ${show(after)}` }];
    } else {
      lines = diffRecord((before as Record<string, unknown>) ?? {}, (after as Record<string, unknown>) ?? {});
    }
    if (lines.length) out.push({ field, label: FIELD_LABEL[field] ?? field, lines });
  }
  return out;
}

/** The playbook a reel was made with, e.g. "v3 (global)". */
export function formatServed(ref: PlaybookRef | undefined): string {
  return ref ? `v${ref.served.version} (${ref.served.scope})` : 'n/a';
}
